import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  deleteTaxGroupField,
  getAllTaxGroups,
  getTaxGroupsFields,
  ITaxGroups,
} from "./TaxGroupsViewController";
import { IPagination } from "../../data/AppInterface";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { deleteIcon, editIcon, eyeIcon } from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { ORDER_BY } from "../../utils/AppConstants";
import TaxGroupForm from "./TaxGroupForm";

let timer: ReturnType<typeof setTimeout>;
const TaxGroupsView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [taxGroupsList, setTaxGroupsList] = useState<ITaxGroups[]>([]);
  const [taxGroupsToEdit, setTaxGroupsToEdit] = useState<ITaxGroups>();
  const { pagination, setChangePagination } = UsePaginationHook();

  useEffect(() => {
    fetchTaxGroupsField({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch]);

  const fetchTaxGroupsField = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await getAllTaxGroups(
        setTaxGroupsList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch
      );
      setIsLoading(false);
    }, 500);
  };

  const customActionView = (item: ITaxGroups) => {
    return (
      <div className="flex item-center justify-center w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setTaxGroupsToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>

        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setTaxGroupsToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setTaxGroupsToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
      </div>
    );
  };
  const handleDelete = async (value: boolean) => {
    if (value && taxGroupsToEdit) {
      const data = await deleteTaxGroupField(taxGroupsToEdit.id);
      if (data) {
        fetchTaxGroupsField(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setTaxGroupsToEdit(undefined);
  };
  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (taxGroupsToEdit) {
      setTaxGroupsToEdit(undefined);
    }
    if (status) {
      fetchTaxGroupsField(pagination);
    }
  };
  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchTaxGroupsField(pagination);
        }}
        title={t("tax_groups")}
      />

      <ICTable<ITaxGroups>
        isLoading={isLoading}
        totalFlex="grid grid-cols-14"
        fields={getTaxGroupsFields(customActionView, t)}
        data={taxGroupsList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchTaxGroupsField({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchTaxGroupsField({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchTaxGroupsField({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <TaxGroupForm
          handleClose={handleCloseForm}
          taxGroupToEdit={taxGroupsToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && taxGroupsToEdit && (
        <ICConfirmationModal
          title={t("tax_group")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", taxGroupsToEdit.name],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default TaxGroupsView;
